import React from 'react';
import { brandMark } from './AppChrome';

/** Display names, since a slug is not always the brand's own capitalisation. */
const TOOL_LABELS: Record<string, string> = {
  slack: 'Slack',
  gmail: 'Gmail',
  notion: 'Notion',
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  intercom: 'Intercom',
  linear: 'Linear',
  github: 'GitHub',
  figma: 'Figma',
  stripe: 'Stripe',
  snowflake: 'Snowflake',
  quickbooks: 'QuickBooks',
  mailchimp: 'Mailchimp',
  sentry: 'Sentry',
  ahrefs: 'Ahrefs',
  granola: 'Granola',
  pipedrive: 'Pipedrive',
  zoho: 'Zoho CRM',
  dynamics365: 'Dynamics 365',
  attio: 'Attio',
  close: 'Close',
  copper: 'Copper',
  freshsales: 'Freshsales',
  monday: 'monday.com',
  zendesk: 'Zendesk'
};

/**
 * The surface lockup used by the AI Co Worker switcher — a platform's mark
 * beside its name — extracted so it can be exported on its own.
 *
 * On the page it lives inside a segmented control, which gives it a cream
 * track and, when selected, an ink fill. Neither belongs to the lockup itself,
 * so this renders the mark and the word alone on nothing: what gets exported
 * is the pill's content, not the control it happens to sit in.
 */

export type SurfacePillTone = 'ink' | 'white';
export type SurfacePillShape = 'bare' | 'pill';
export function SurfacePill({
  logo,
  label,
  tone = 'ink',
  shape = 'pill',
  size = 15
}: {
  logo: string;
  label: string;
  /** Ink for light backgrounds, white for dark ones. */
  tone?: SurfacePillTone;
  /** A translucent capsule, or the mark and word alone. */
  shape?: SurfacePillShape;
  /** Mark size in px; everything else scales with it. */
  size?: number;
}) {
  const dark = tone === 'white';
  // Translucent rather than solid so the lockup keeps a readable plate on a
  // photo or a busy panel without becoming a second, competing surface. The
  // hairline is what stops it dissolving on a light background.
  const capsule: React.CSSProperties = shape === 'pill' ? {
    backgroundColor: dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.62)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.22)' : 'rgba(43, 33, 26, 0.12)'}`,
    borderRadius: 999,
    padding: `${size * 0.42}px ${size * 0.72}px`
  } : {};
  return <span className="inline-flex items-center font-semibold" style={{
    gap: size * 0.4,
    fontSize: size * 0.95,
    color: dark ? '#FFFFFF' : '#2B211A',
    ...capsule
  }} data-id="element-1026" data-mp="1kqkwovif504w">
      <img src={logo} alt="" aria-hidden="true" className="shrink-0 object-contain" style={{
      width: size,
      height: size
    }} data-id="element-1027" data-mp="1bsj0ct9mqvny" />
      {label}
    </span>;
}
export type PillAsset = {
  id: string;
  label: string;
  logo: string;
};

/** Every surface the co worker runs in, in the order the page lists them. */
export const SURFACE_PILLS: PillAsset[] = [{
  id: 'slack',
  label: 'Slack',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg'
}, {
  id: 'teams',
  label: 'Teams',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg'
}, {
  id: 'claude',
  label: 'Claude',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/claude-ai.svg'
}, {
  id: 'chatgpt',
  label: 'ChatGPT',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/chatgpt.svg'
}];

/**
 * The connected tools named across the site, as the same lockup.
 *
 * Marks resolve through the shared brand registry rather than being listed as
 * URLs here, so a tool's logo is fixed in one place and every surface — the
 * connections map, the product screens, these exports — picks the change up.
 */
const asPills = (slugs: string[]): PillAsset[] => slugs.map(slug => ({
  id: slug,
  label: TOOL_LABELS[slug] ?? slug,
  logo: brandMark(slug)
}));
export const TOOL_PILLS: PillAsset[] = asPills(['slack', 'gmail', 'notion', 'intercom', 'linear', 'github', 'figma', 'stripe', 'snowflake', 'quickbooks', 'mailchimp', 'sentry', 'ahrefs', 'granola']);

/**
 * CRMs kept as their own set. Salesforce and HubSpot lead because they are the
 * two the site already names; the rest cover what a mid-market buyer is most
 * likely to be running instead.
 */
export const CRM_PILLS: PillAsset[] = asPills(['salesforce', 'hubspot', 'pipedrive', 'zoho', 'dynamics365', 'attio', 'close', 'copper', 'freshsales', 'monday', 'zendesk']);